/**
 * FilterPill — the filter-trigger pill (UX-13 S0, promoted from the
 * FilterBar grammar with the geometry ruling applied): rounded-base at
 * the segmented toggle's density (4px vertical / 14px horizontal), the
 * standard sage-400 focus ring, an optional count badge, and the 36px
 * touch floor via the same invisible before: extension the Button
 * primitive carries — the rendered box tightens, the target never does.
 *
 * Active state: sage-800 fill on cream text (the selected filter is a
 * commitment, not a hint); rest state paper ground with the sage-300
 * hairline. Consumers own what opens (BottomSheet dropdowns per the
 * mobile-correct ruling); this is only the trigger.
 */

import { type ComponentPropsWithRef, type ReactNode } from 'react';

export interface FilterPillProps extends ComponentPropsWithRef<'button'> {
  label: string;
  /** The badge's count (a selection, or what a map layer draws); renders
   *  when > 0, grouped by thousands. */
  count?: number;
  active?: boolean;
  /**
   * Leading glyph slot (UX-13 S1 additive) — boolean-toggle pills carry
   * a state glyph (e.g. the "Has notes" check); dropdown triggers don't.
   */
  icon?: ReactNode;
}

export function FilterPill({
  label,
  count = 0,
  active = false,
  icon,
  className,
  ...rest
}: FilterPillProps): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={[
        'relative inline-flex shrink-0 items-center gap-1.5 rounded-base border px-3.5 py-1',
        'text-[13px] font-medium whitespace-nowrap cursor-pointer',
        'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400',
        // 36px touch floor: invisible hit-area extension (the Button mechanism).
        "before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']",
        active
          ? 'bg-sage-800 border-sage-800 text-cream'
          : 'bg-paper border-sage-300 text-ink hover:border-sage-400 hover:text-sage-800',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {icon}
      {label}
      {count > 0 && (
        <span
          data-testid="filter-pill-count"
          className={
            'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-base text-xs font-semibold ' +
            (active ? 'bg-cream/20 text-cream' : 'bg-sage-50 text-sage-600')
          }
        >
          {count.toLocaleString('en-US')}
        </span>
      )}
    </button>
  );
}
